import { useState, useEffect, useCallback } from 'react';
import { sessionAPI } from '../services/api';
import { listAllRoomsWithStatus, getRoomDevices, reserveRoom, releaseRoom, RoomWithAvailability, RoomDevice } from '../services/rooms';
import { listDevicesViaN8n, DeviceInfo } from '../services/n8n';

export interface RoomDeviceBinding {
  roomId: string | null;
  roomName: string | null;
  rooms: RoomWithAvailability[];
  selectedRoomId: string;
  setSelectedRoomId: (id: string) => void;
  roomDevices: RoomDevice[];
  allDevices: DeviceInfo[];
  bindingRoom: boolean;
  bindRoom: () => Promise<void>;
  releaseBoundRoom: () => Promise<void>;
  reload: () => Promise<void>;
  error: string | null;
}

// Room-then-device allocation (mirrors DevicesTab.tsx / device-n8n.ts): pair
// a room to the linked session, then pair a device already mapped to that
// room to a role. Originally lived inline in CastSetupPage (pre-run); shared
// here so RunConsolePage (during) and ResultsPage (after) can offer the same
// seat-to-device reassignment without duplicating the room-binding state.
export function useRoomDeviceBinding(sessionId: string | undefined): RoomDeviceBinding {
  const [roomId, setRoomId] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [rooms, setRooms] = useState<RoomWithAvailability[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [roomDevices, setRoomDevices] = useState<RoomDevice[]>([]);
  const [allDevices, setAllDevices] = useState<DeviceInfo[]>([]);
  const [bindingRoom, setBindingRoom] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!sessionId) return;
    const session = await sessionAPI.getSession(sessionId);
    if (session.roomId) {
      setRoomId(session.roomId);
      setRoomName(session.roomName || session.roomId);
      const [devices, allDevs] = await Promise.all([getRoomDevices(session.roomId), listDevicesViaN8n()]);
      setRoomDevices(devices);
      setAllDevices(allDevs.devices);
    } else {
      setRoomId(null);
      setRoomName(null);
      setRoomDevices([]);
    }
  }, [sessionId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!sessionId || roomId) return;
    listAllRoomsWithStatus().then(setRooms).catch(() => {});
  }, [sessionId, roomId]);

  const bindRoom = async () => {
    if (!selectedRoomId || !sessionId) return;
    setBindingRoom(true);
    setError(null);
    try {
      await reserveRoom(selectedRoomId, { sessionId });
      await reload();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBindingRoom(false);
    }
  };

  const releaseBoundRoom = async () => {
    if (!roomId || !sessionId) return;
    setBindingRoom(true);
    try {
      await releaseRoom(roomId, sessionId);
      setRoomId(null);
      setRoomName(null);
      setRoomDevices([]);
    } finally {
      setBindingRoom(false);
    }
  };

  return { roomId, roomName, rooms, selectedRoomId, setSelectedRoomId, roomDevices, allDevices, bindingRoom, bindRoom, releaseBoundRoom, reload, error };
}
